import S from './GradientFlowStyles';

type Props = {
  data: any;
  showHours: boolean;
};

const colors = [
  '#9f130a', // 0
  '#a9251c', // 1
  '#d9392f', // 2
  '#fc4c41', // 3
  '#b88b49', // 4
  '#d5b27f', // 5
  '#edb562', // 6
  '#fecc82', // 7
  '#13875d', // 8
  '#41c193', // 9
  '#51dfac', // 10
];

/** Map 0–100 scores to the 1–10 color buckets this chart was built for. */
const scoreToColorIndex = (score: number): number => {
  if (!Number.isFinite(score) || score <= 0) return 1;
  return Math.min(10, Math.max(1, Math.round(score / 10)));
};

function GradientFlow({ data, showHours }: Props): JSX.Element {
  const labels = data ? data.map((l: any) => l.scoreDate) : [];

  const getTrend = (prev: number, next: number): string => {
    const delta = next - prev;
    let trend;
    // Thresholds are on the 0–100 score scale (±2% minor, ±15% major).
    if (delta >= 2) {
      trend = delta >= 15 ? 'up' : 'mid-up';
    } else if (delta <= -2) {
      trend = delta <= -15 ? 'down' : 'mid-down';
    } else {
      trend = 'same';
    }
    return trend;
  };

  const getArrow = (prev: number, current: number, next: number): any => {
    const noTrend = prev != null && getTrend(prev, current) === getTrend(current, next);
    return noTrend ? (<div className="hidden" />) : (<div className={getTrend(current, next)}>&#8594;</div>);
  };

  const getGradientStyle = (from: number, to: number): any => {
    const fromValue = scoreToColorIndex(from);
    const toValue = scoreToColorIndex(to);
    const style = {
      background: `linear-gradient(to right, ${colors[fromValue - 1]}, ${colors[toValue - 1]})`,
    };
    return style;
  };

  const getTimeLabel = (time: string): string => {
    const d = new Date(time);
    const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!showHours || data.length <= 1) return day;
    return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
  };

  if (!data || data.length === 0) {
    return (<S.Wrapper><div className="flow no-data">No data available for selected period</div></S.Wrapper>);
  }

  return (
    <S.Wrapper>
      <div className="flow">
        {data.map((elem: any, i: number) => {
          const currentValue = elem.score;
          const prevValue = i > 0 ? data[i - 1].score : undefined;
          const nextValue = i < (data.length - 1) ? data[i + 1].score : currentValue;
          const key = i;
          return (
            <S.Sub className="sub" key={key} $count={data.length}>
              {/* <div style={getGradientStyle(prevValue, currentValue)} className="bar">{elem.data}</div> */}
              <div style={getGradientStyle(currentValue, currentValue)} className="bar"><div className="hidden" /></div>
              <div style={getGradientStyle(currentValue, nextValue)} className="bar">
                {getArrow(prevValue, currentValue, nextValue)}
              </div>
            </S.Sub>
          );
        })}
      </div>
      <div className="timeline">
        {labels.map((label: any, j: number) => (
          <S.Sub className="time" key={label} $count={data.length}>
            {labels.length > 20 && (j % 2) ? (<p className="hidden">-</p>) : (<p>{getTimeLabel(label)}</p>)}
          </S.Sub>
        ))}
      </div>
    </S.Wrapper>
  );
}

export default GradientFlow;
